/**
 * Renders DM / internal-chat attachments (images, voice, files).
 * File cards use a light surface so they stay readable inside blue outgoing bubbles.
 */

import React from "react";
import { Box, Stack, Typography } from "@mui/material";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import MicIcon from "@mui/icons-material/Mic";
import { colorTokens, radiusTokens } from "@/widgets/frontdesk/design-system";

export type InternalMessageAttachment = {
  id?: string;
  name?: string;
  mimeType?: string;
  size?: number;
  kind?: "image" | "file" | "voice" | string;
  dataUrl?: string;
  data_url?: string;
  url?: string;
};

function resolveSrc(att: InternalMessageAttachment): string | undefined {
  const src = att.dataUrl || att.data_url || att.url;
  return typeof src === "string" && src.trim() ? src.trim() : undefined;
}

function isVoice(att: InternalMessageAttachment): boolean {
  if (att.kind === "voice") return true;
  const mime = String(att.mimeType || "").toLowerCase();
  return mime.startsWith("audio/");
}

function isImage(att: InternalMessageAttachment): boolean {
  if (att.kind === "image") return true;
  const mime = String(att.mimeType || "").toLowerCase();
  return mime.startsWith("image/");
}

function formatFileSize(bytes?: number): string | null {
  if (typeof bytes !== "number" || !Number.isFinite(bytes) || bytes <= 0) {
    return null;
  }
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function getExtension(name?: string, mimeType?: string): string {
  const fromName = String(name || "").split(".").pop()?.trim();
  if (fromName && fromName !== name && fromName.length <= 8) {
    return fromName.toUpperCase();
  }
  const mime = String(mimeType || "").toLowerCase();
  if (mime.includes("pdf")) return "PDF";
  if (mime.includes("word") || mime.includes("document")) return "DOC";
  if (mime.startsWith("image/")) return "IMG";
  return "FILE";
}

function getFileIcon(mimeType?: string, name?: string) {
  const mime = String(mimeType || "").toLowerCase();
  const lowerName = String(name || "").toLowerCase();
  if (mime.includes("pdf") || lowerName.endsWith(".pdf")) {
    return {
      icon: <PictureAsPdfOutlinedIcon sx={{ fontSize: 22 }} />,
      color: colorTokens.error[600],
      bg: colorTokens.error[50],
    };
  }
  if (
    mime.includes("word") ||
    mime.includes("document") ||
    lowerName.endsWith(".doc") ||
    lowerName.endsWith(".docx")
  ) {
    return {
      icon: <DescriptionOutlinedIcon sx={{ fontSize: 22 }} />,
      color: colorTokens.info[600],
      bg: colorTokens.info[50],
    };
  }
  if (mime.startsWith("image/") || isImage({ mimeType, kind: "image" })) {
    return {
      icon: <ImageOutlinedIcon sx={{ fontSize: 22 }} />,
      color: colorTokens.primary[600],
      bg: colorTokens.primary[50],
    };
  }
  return {
    icon: <InsertDriveFileOutlinedIcon sx={{ fontSize: 22 }} />,
    color: colorTokens.neutral[600],
    bg: colorTokens.neutral[100],
  };
}

export function getInternalMessageAttachments(
  message: { metadata?: unknown; content?: string } | null | undefined,
): InternalMessageAttachment[] {
  const meta = message?.metadata as
    | { attachments?: InternalMessageAttachment[] }
    | undefined;
  if (Array.isArray(meta?.attachments)) return meta.attachments;
  return [];
}

export function isInternalMediaPlaceholder(content?: string | null): boolean {
  const text = String(content || "").trim();
  return (
    /^(🎤\s*)?Voice message(\s*\(\d+s\))?$/i.test(text) ||
    /^(📷\s*)?Image$/i.test(text) ||
    /^(📎\s*)?Attachment$/i.test(text)
  );
}

const FileAttachmentCard: React.FC<{
  att: InternalMessageAttachment;
  src?: string;
}> = ({ att, src }) => {
  const fileName = att.name || "Attachment";
  const extension = getExtension(att.name, att.mimeType);
  const sizeLabel = formatFileSize(att.size);
  const { icon, color, bg } = getFileIcon(att.mimeType, att.name);

  const content = (
    <>
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: `${radiusTokens.md}px`,
          backgroundColor: bg,
          color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: "0.8125rem",
            fontWeight: 600,
            color: colorTokens.neutral[900],
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            lineHeight: 1.35,
          }}
          title={fileName}
        >
          {fileName}
        </Typography>
        <Typography
          sx={{
            fontSize: "0.6875rem",
            fontWeight: 500,
            color: colorTokens.neutral[500],
            textTransform: "uppercase",
            letterSpacing: "0.02em",
            mt: 0.15,
          }}
        >
          {extension}
          {sizeLabel ? ` · ${sizeLabel}` : ""}
        </Typography>
      </Box>
      {src ? (
        <DownloadOutlinedIcon
          sx={{
            fontSize: 18,
            color: colorTokens.neutral[400],
            flexShrink: 0,
          }}
        />
      ) : null}
    </>
  );

  const cardSx = {
    display: "flex",
    alignItems: "center",
    gap: 1.5,
    px: 1.5,
    py: 1.15,
    minWidth: 220,
    maxWidth: 300,
    backgroundColor: "#FFFFFF",
    border: `1px solid ${colorTokens.neutral[200]}`,
    borderRadius: `${radiusTokens.md}px`,
    boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
    textDecoration: "none",
    color: "inherit",
    transition: "background-color 0.15s ease, border-color 0.15s ease",
    "&:hover": src
      ? {
          backgroundColor: colorTokens.neutral[50],
          borderColor: colorTokens.neutral[300],
        }
      : undefined,
  } as const;

  if (src) {
    return (
      <Box
        component="a"
        href={src}
        download={fileName}
        target="_blank"
        rel="noopener noreferrer"
        sx={cardSx}
      >
        {content}
      </Box>
    );
  }

  return <Box sx={cardSx}>{content}</Box>;
};

export const InternalMessageAttachments: React.FC<{
  attachments: InternalMessageAttachment[];
}> = ({ attachments }) => {
  if (!attachments.length) return null;

  return (
    <Stack spacing={1} sx={{ mt: 0.75, maxWidth: "100%" }}>
      {attachments.map((att, index) => {
        const key = att.id || `${att.name || "att"}-${index}`;
        const src = resolveSrc(att);

        if (isImage(att) && src) {
          return (
            <Box
              key={key}
              component="a"
              href={src}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: "block",
                maxWidth: "100%",
                overflow: "hidden",
                borderRadius: `${radiusTokens.md}px`,
                border: `1px solid ${colorTokens.neutral[200]}`,
                backgroundColor: "#FFFFFF",
                boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
              }}
            >
              <Box
                component="img"
                src={src}
                alt={att.name || "Image"}
                sx={{
                  maxWidth: "100%",
                  maxHeight: 240,
                  display: "block",
                  objectFit: "contain",
                  backgroundColor: colorTokens.neutral[50],
                }}
              />
            </Box>
          );
        }

        if (isVoice(att)) {
          return (
            <Box
              key={key}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                px: 1.25,
                py: 1,
                minWidth: 220,
                maxWidth: "100%",
                backgroundColor: "#FFFFFF",
                border: `1px solid ${colorTokens.neutral[200]}`,
                borderRadius: `${radiusTokens.md}px`,
                boxShadow: "0 1px 2px rgba(16, 24, 40, 0.06)",
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "50%",
                  backgroundColor: colorTokens.primary[50],
                  color: colorTokens.primary[600],
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <MicIcon sx={{ fontSize: 18 }} />
              </Box>
              {src ? (
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <audio
                    controls
                    preload="metadata"
                    src={src}
                    style={{ width: "100%", height: 32, verticalAlign: "middle" }}
                  >
                    <track kind="captions" />
                  </audio>
                </Box>
              ) : (
                <Typography
                  sx={{
                    fontSize: "0.8125rem",
                    fontWeight: 600,
                    color: colorTokens.neutral[800],
                  }}
                >
                  {att.name || "Voice message"}
                </Typography>
              )}
            </Box>
          );
        }

        return <FileAttachmentCard key={key} att={att} src={src} />;
      })}
    </Stack>
  );
};
